import React, { useState, useEffect } from 'react';
import { Card } from '../Card'; // Assumendo l'esistenza di questo componente
import { Subject } from '../../model/registration/SchoolSelection';
import { loadSubjectIcons } from '../../utils/loadSvg';
import useScreenWidth from '../../hooks/useScreenWidth';
import Loading from '../Loading';

interface SubjectsSelectionProps {
  options: Subject[];
  onSubjectsSelection: (subjects: string[]) => void;
  setSelectedSchoolSubjects: (selectedSchoolSubjects: string[]) => void;
}

const SubjectsSelection: React.FC<SubjectsSelectionProps> = ({
  options,
  onSubjectsSelection,
  setSelectedSchoolSubjects,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [images, setImages] = useState<{ [key: string]: string }>({}); // Stato per le immagini
  const [imagesLoading, setImagesLoading] = useState(true);
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth < 640;

  useEffect(() => {
    // Carica in parallelo le immagini per tutte le materie
    const loadImages = async () => {
      setImagesLoading(true);
      setImages(await loadSubjectIcons(options.map((option) => option.name)));
      setImagesLoading(false);
    };

    loadImages();
  }, [options]); // Esegui ogni volta che le opzioni cambiano

  const handleSelection = (option: Subject) => {
    const isSelected = selectedOptions.find((o) => o === option.name);
    if (isSelected) {
      setSelectedOptions(selectedOptions.filter((o) => o !== option.name));
    } else {
      setSelectedOptions([...selectedOptions, option.name]);
    }
  };

  const next = () => {
    if (selectedOptions.length === 0) {
      return;
    }
    setSelectedSchoolSubjects(selectedOptions);
    onSubjectsSelection(selectedOptions);
  };

  return (
    <div className="w-full bg-white rounded-lg sm:rounded-xl px-6 py-3 sm:p-6 lg:p-8 shadow-lg">
      {imagesLoading && <Loading />}
      <div className="text-center mb-4 sm:mb-6 lg:mb-8">
        <h2 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold text-black mb-1 sm:mb-2">
          Di quali materie ha bisogno?
        </h2>
        <p className="text-xs sm:text-sm lg:text-base text-gray-600 px-2">
          Seleziona una o più materie per le ripetizioni
        </p>
        {selectedOptions.length > 0 && (
          <div className="mt-3 sm:mt-4 inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm bg-fpcpink text-fpcred font-medium">
            {selectedOptions.length} {selectedOptions.length === 1 ? 'materia' : 'materie'}{' '}
            {selectedOptions.length === 1 ? 'selezionata' : 'selezionate'}
          </div>
        )}
      </div>

      <div className="flex flex-wrap justify-center gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8 max-w-4xl mx-auto">
        {options.map((option, index) => (
          <Card
            key={index}
            title={option.name}
            onClick={() => handleSelection(option)}
            selected={!!selectedOptions.find((o) => o === option.name)}
            imageSrc={images[option.name]}
            size={isMobile ? 'small' : 'default'}
          />
        ))}
      </div>

      <div className="flex justify-center px-2">
        <button
          disabled={selectedOptions.length === 0}
          onClick={() => next()}
          className="px-4 sm:px-6 lg:px-8 py-2 sm:py-3 bg-fpcred text-white font-semibold rounded-lg sm:rounded-xl hover:bg-buttonhover disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-fpcred transition-all duration-200 transform hover:scale-105 shadow-lg text-sm sm:text-base">
          Procedi{selectedOptions.length > 0 ? ` (${selectedOptions.length})` : ''}
        </button>
      </div>
    </div>
  );
};

export default SubjectsSelection;
